import * as Sentry from '@sentry/react'
import connectApi from 'api/connectApi'
import {
    SENTRY_SMOKE_TEST_TAG,
    SENTRY_SMOKE_TEST_FINGERPRINT,
    SMOKE_TEST_HEADER
} from './sentrySmokeTestConstants'

export { SENTRY_SMOKE_TEST_TAG, SENTRY_SMOKE_TEST_FINGERPRINT }

export type SentrySmokeTestScenarioId =
    | 'manual_exception'
    | 'uncaught_exception'
    | 'unhandled_rejection'
    | 'network_failure'
    | 'timeout'
    | 'bad_response'
    | 'render_error'

export interface SentrySmokeTestScenario {
    id: SentrySmokeTestScenarioId
    label: string
    description: string
}

// Each scenario is described here so the debug page can present a
// "choose your error" selector without duplicating this knowledge.
export const SENTRY_SMOKE_TEST_SCENARIOS: SentrySmokeTestScenario[] = [
    {
        id: 'manual_exception',
        label: 'Manual exception',
        description:
            'Calls Sentry.captureException directly, like most catch blocks in the app.'
    },
    {
        id: 'uncaught_exception',
        label: 'Uncaught exception',
        description:
            "Throws an error that's never caught, like a bug nobody wrapped in try/catch. Exercises Sentry's global error handler."
    },
    {
        id: 'unhandled_rejection',
        label: 'Unhandled promise rejection',
        description:
            "Rejects a promise with no .catch, e.g. a forgotten await. Exercises Sentry's global unhandled-rejection handler."
    },
    {
        id: 'network_failure',
        label: 'Network failure',
        description:
            "Requests a host that can't resolve, through the real API client, to exercise connectApi's network-error reporting."
    },
    {
        id: 'timeout',
        label: 'Timeout',
        description:
            "Requests the real API with a near-zero timeout, to exercise connectApi's timeout reporting."
    },
    {
        id: 'bad_response',
        label: 'Bad HTTP response (5xx)',
        description:
            "Requests a public test endpoint that always returns HTTP 500, through the real API client, to exercise connectApi's bad-response reporting."
    },
    {
        id: 'render_error',
        label: 'React render error',
        description:
            'Throws during render to exercise the top-level Sentry.ErrorBoundary. This replaces the whole app UI with the error screen - reload the page afterwards.'
    }
]

const createSmokeTestId = () => new Date().toISOString()

const smokeTestHeaders = { [SMOKE_TEST_HEADER]: 'true' }

// Any request/network errors triggered here are already reported by
// connectApi's interceptor - swallow them so they don't also surface as an
// unhandled rejection in the browser console.
const ignoreAlreadyReportedError = () => undefined

export const sendSentrySmokeTestEvent = () => {
    const testId = createSmokeTestId()

    Sentry.captureException(
        new Error(`Connect Sentry smoke test - manual exception (${testId})`),
        {
            level: 'debug',
            tags: { [SENTRY_SMOKE_TEST_TAG]: 'true' },
            fingerprint: [SENTRY_SMOKE_TEST_FINGERPRINT]
        }
    )

    return testId
}

export const triggerUncaughtExceptionSmokeTest = () => {
    const testId = createSmokeTestId()

    // Deferred and outside of React's render phase, so this is NOT caught by
    // the ErrorBoundary - it becomes a genuinely uncaught error, exercising
    // Sentry's global `onerror` instrumentation instead of an explicit
    // captureException call.
    setTimeout(() => {
        throw new Error(
            `Connect Sentry smoke test - uncaught exception (${testId})`
        )
    }, 0)

    return testId
}

export const triggerUnhandledRejectionSmokeTest = () => {
    const testId = createSmokeTestId()

    // Deliberately not awaited/caught - exercises Sentry's global
    // `unhandledrejection` instrumentation.
    Promise.reject(
        new Error(
            `Connect Sentry smoke test - unhandled rejection (${testId})`
        )
    )

    return testId
}

export const triggerNetworkFailureSmokeTest = async () => {
    const testId = createSmokeTestId()

    // `.invalid` is a reserved TLD (RFC 2606) guaranteed to fail DNS
    // resolution, so this reliably exercises the "no response" branch of
    // connectApi's interceptor without depending on a third party service.
    await connectApi
        .get(`https://connect-sentry-smoke-test.invalid/${testId}`, {
            headers: smokeTestHeaders
        })
        .catch(ignoreAlreadyReportedError)

    return testId
}

export const triggerTimeoutSmokeTest = async () => {
    const testId = createSmokeTestId()

    await connectApi
        .get('/', {
            timeout: 1,
            headers: smokeTestHeaders
        })
        .catch(ignoreAlreadyReportedError)

    return testId
}

export const triggerBadResponseSmokeTest = async () => {
    const testId = createSmokeTestId()

    // httpstat.us reliably returns the requested status. Auth is stripped by
    // connectApi for external absolute URLs so the bearer token is not leaked.
    await connectApi
        .get('https://httpstat.us/500', { headers: smokeTestHeaders })
        .catch(ignoreAlreadyReportedError)

    return testId
}
